import { useState, useEffect, useCallback } from 'react';
import { supabase, EDGE_BASE } from '@/lib/supabase';

export interface CustomerInfo {
  id: string;
  customer_code: string;
  full_name: string;
  email: string | null;
  phone: string | null;
}

const SESSION_KEY = 'daeg_customer_session';

export function useCustomerAuth() {
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) setCustomer(JSON.parse(raw));
    } catch {
      // ignore
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (customerCode: string, password: string) => {
    const res = await fetch(`${EDGE_BASE}/customer-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_code: customerCode, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Đăng nhập thất bại.');
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data.customer));
    setCustomer(data.customer);
    return data.customer as CustomerInfo;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setCustomer(null);
  }, []);

  return { customer, loading, login, logout, isLoggedIn: !!customer };
}

// Fetch full case data for a customer
export async function fetchCustomerCase(customerId: string) {
  const { data: cases, error } = await supabase
    .from('cases')
    .select('*')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })
    .limit(1);

  if (error || !cases || cases.length === 0) return null;
  const caseData = cases[0];

  const [stepsRes, docsRes, notifsRes, assignmentsRes] = await Promise.all([
    supabase.from('case_steps').select('*').eq('case_id', caseData.id).order('step_order'),
    supabase.from('case_documents').select('*').eq('case_id', caseData.id).order('updated_at'),
    supabase.from('case_notifications').select('*').eq('case_id', caseData.id).order('created_at', { ascending: false }),
    supabase
      .from('case_staff_assignments')
      .select('admin_users(full_name, email, role)')
      .eq('case_id', caseData.id),
  ]);

  const assignedStaff = (assignmentsRes.data || [])
    .map((a: { admin_users: { full_name: string; email: string; role: string } | null }) => a.admin_users)
    .filter(Boolean) as { full_name: string; email: string; role: string }[];

  return {
    ...caseData,
    steps: stepsRes.data || [],
    documents: docsRes.data || [],
    notifications: notifsRes.data || [],
    assignedStaff,
  };
}

export async function markNotificationRead(notifId: string) {
  await supabase.from('case_notifications').update({ is_read: true }).eq('id', notifId);
}
